import React, { useMemo } from 'react'
import { addMinutes, differenceInMinutes, format } from 'date-fns'
import { Icon } from '@chakra-ui/react'
import { useTheme } from '@chakra-ui/react'
import { Box, Text, HStack } from '@chakra-ui/react'

import {
    AppointmentFieldsFragment,
    AppointmentStatus,
} from '~graphql/generated/graphql'
import fontColorContrast from 'font-color-contrast'
import { FaPaw, FaUser } from 'react-icons/fa'
import { Tag } from '@chakra-ui/react'
import { ImArrowRight } from 'react-icons/im'
import {
    getAppointmentStatusFromAppointment,
    getClientNameFromAppointment,
    getPatientNameFromAppointment,
    getServiceNameFromAppointment,
    getSpeciesNameFromAppointment,
} from '~utils/appointment-helpers'
import { stringIfExists } from '~utils/helpers'

export const AppointmentItem: React.FC<{
    appointment: AppointmentFieldsFragment
    startTime: number
    index: number
    spacing?: number
    isConflicting?: boolean
    isCurrent?: boolean
}> = ({
    appointment,
    startTime,
    index,
    spacing = 56,
    isConflicting,
    isCurrent,
}) => {
        const theme = useTheme()

        const startDate = new Date(appointment.start_time)
        const endDate = new Date(
            addMinutes(appointment.start_time, appointment.minutes)
        )

        const textColor = appointment.service?.color
            ? fontColorContrast(appointment.service.color) === '#000000'
                ? 'rgba(0, 0, 0, 0.9)'
                : 'rgba(255, 255, 255, 0.9)'
            : 'rgba(0, 0, 0, 0.9)'

        const iconColor = appointment.service?.color
            ? fontColorContrast(appointment.service.color) === '#000000'
                ? 'rgba(0, 0, 0, 0.75)'
                : 'rgba(255, 255, 255, 0.75)'
            : 'rgba(0, 0, 0, 0.75)'

        const startHours = startDate.getHours()
        const startMinutes = startDate.getMinutes()

        const top =
            ((startHours - Math.floor(startTime)) * 4 + startMinutes / 15) *
            spacing +
            2

        const MIN_HEIGHT = 60
        const height = (differenceInMinutes(endDate, startDate) * spacing) / 15 - 3

        const status = getAppointmentStatusFromAppointment(appointment)

        const bgColor = useMemo(() => {
            if (status === AppointmentStatus.Cancelled)
                return theme.colors.danger[300]
            return appointment.service?.color || theme.colors.brand[300]
        }, [appointment, theme.colors.yellow])

        return (
            <>
                <Box
                    key={'appt' + index}
                    position="absolute"
                    top={`${top}px`}
                    left={isConflicting ? 'calc(50%)' : '4px'}
                    w={isConflicting ? `calc(50% - 4px)` : `calc(100% - 8px)`}
                    px={1}
                    role="group"
                    transition="0.1s"
                    zIndex={0}
                    bgColor={bgColor}
                    opacity={isCurrent ? 1 : 0.6}
                    boxShadow={isCurrent ? 'xl' : 'none'}
                    minH={`${height}px`}
                    // height={`${height}px`}
                    maxH={`${height}px`}
                    overflow="hidden"
                    rounded="md"
                    textAlign="left"
                >
                    <Box pt={height > MIN_HEIGHT ? 3 : 1} px={4} pb={3}>
                        <Text
                            noOfLines={1}
                            letterSpacing="wide"
                            fontWeight="bold"
                            fontSize="15px"
                            lineHeight="17px"
                            color={textColor}
                        >
                            {stringIfExists(
                                getServiceNameFromAppointment(appointment),
                                {
                                    posttext: ' appointment',
                                }
                            ) || 'Appointment'}
                        </Text>
                        <Text
                            color={textColor}
                            fontSize="13px"
                            fontWeight="semibold"
                            noOfLines={1}
                        >
                            {format(startDate, 'h:mm a')} -{' '}
                            {format(endDate, 'h:mm a')}
                        </Text>
                        <HStack alignItems="center">
                            <Icon as={FaPaw} boxSize="13px" color={iconColor} />
                            <Text
                                color={textColor}
                                letterSpacing="wide"
                                fontWeight="semibold"
                                fontSize="13px"
                                noOfLines={1}
                            >
                                {getPatientNameFromAppointment(appointment)}
                                {stringIfExists(
                                    getSpeciesNameFromAppointment(appointment),
                                    {
                                        pretext: ' (',
                                        posttext: ')',
                                    }
                                )}
                            </Text>
                        </HStack>
                        <HStack alignItems="center">
                            <Icon as={FaUser} boxSize="13px" color={iconColor} />
                            <Text
                                color={textColor}
                                letterSpacing="wide"
                                fontSize="13px"
                                fontWeight="semibold"
                                noOfLines={1}
                            >
                                {getClientNameFromAppointment(appointment)}
                            </Text>
                        </HStack>
                    </Box>
                </Box>
            </>
        )
    }
